import { Action, ActionReducer, createReducer, createSelector, on } from '@ngrx/store';
import { CitationItem, CitationWithId } from '@sciflow/cite';
import { Reference } from '../reference.model';
import * as referenceActions from './references.actions';

export const referenceFeatureKey = 'reference-management';
export type ReferenceSortOrder = 'none' | 'author';

/**
 * Collects all valid identifiers that can be used to match a reference.
 */
const getReferenceIds = (reference: Reference): string[] => {
  const ids = [reference?.key, reference?.csl?.id]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean);
  return Array.from(new Set(ids));
};

/**
 * Returns true when a reference can be addressed by the provided identifier.
 */
const matchesReferenceId = (reference: Reference, referenceId: string): boolean =>
  getReferenceIds(reference).includes(referenceId);

/**
 * Returns a stable id string for a citation item.
 */
const getCitationItemId = (item?: Pick<CitationItem, 'id'>): string =>
  typeof item?.id === 'string' ? item.id.trim() : '';

/**
 * Merges an incoming citation item over an existing one while preserving values
 * that are not part of the incoming payload.
 */
const mergeCitationItem = (
  existingItem: CitationItem | undefined,
  incomingItem: CitationItem,
): CitationItem => {
  if (!existingItem) {
    return incomingItem;
  }
  return {
    ...existingItem,
    ...incomingItem,
    id: incomingItem?.id !== undefined ? incomingItem.id : existingItem?.id,
    label: incomingItem?.label !== undefined ? incomingItem.label : existingItem?.label,
    locator: incomingItem?.locator !== undefined ? incomingItem.locator : existingItem?.locator,
    prefix: incomingItem?.prefix !== undefined ? incomingItem.prefix : existingItem?.prefix,
    suffix: incomingItem?.suffix !== undefined ? incomingItem.suffix : existingItem?.suffix,
    'suppress-author':
      incomingItem?.['suppress-author'] !== undefined
        ? incomingItem['suppress-author']
        : existingItem?.['suppress-author'],
    'author-only':
      incomingItem?.['author-only'] !== undefined
        ? incomingItem['author-only']
        : existingItem?.['author-only'],
    itemData: incomingItem?.itemData !== undefined ? incomingItem.itemData : existingItem?.itemData,
  };
};

/**
 * Merges incoming citation items with existing citation items by index and id.
 */
const mergeCitationItems = (existingItems: CitationItem[] = [], incomingItems: CitationItem[] = []) =>
  incomingItems.map((incomingItem, index) => {
    const indexedExisting = existingItems[index];
    if (indexedExisting && getCitationItemId(indexedExisting) === getCitationItemId(incomingItem)) {
      return mergeCitationItem(indexedExisting, incomingItem);
    }
    const existingWithSameId = existingItems.find(
      (existingItem) => getCitationItemId(existingItem) === getCitationItemId(incomingItem),
    );
    return mergeCitationItem(existingWithSameId, incomingItem);
  });

/**
 * Merges citations from the editor plugin with citations already in the store.
 */
const mergeCitations = (
  existingCitations: CitationWithId[] = [],
  incomingCitations: CitationWithId[] = [],
): CitationWithId[] => {
  const existingById = new Map<string, CitationWithId>(
    existingCitations
      .filter((citation) => typeof citation?.citationID === 'string')
      .map((citation) => [citation.citationID, citation]),
  );

  return incomingCitations.map((incomingCitation) => {
    const existingCitation = existingById.get(incomingCitation.citationID);
    if (!existingCitation) {
      return incomingCitation;
    }
    return {
      ...existingCitation,
      ...incomingCitation,
      citationMode: incomingCitation.citationMode ?? existingCitation.citationMode,
      plainCitation: incomingCitation.plainCitation ?? existingCitation.plainCitation,
      citationItems: mergeCitationItems(
        existingCitation.citationItems || [],
        incomingCitation.citationItems || [],
      ),
      properties: {
        ...(existingCitation.properties || { noteIndex: 0 }),
        ...(incomingCitation.properties || {}),
      },
    };
  });
};

export interface ReferencesState {
  references: Reference[];
  citations: CitationWithId[];
  activeReferenceId: string | null;
  referenceIdToEdit: string | null;
  sortOrder: ReferenceSortOrder;
}

const initialState: ReferencesState = {
  references: [],
  citations: [],
  activeReferenceId: null,
  referenceIdToEdit: null,
  sortOrder: 'none',
};

export const referenceReducer = createReducer(
  initialState,
  on(referenceActions.addReferences, (state, action) => ({
    ...state,
    references: [...action.references, ...state.references].filter(
      (r, i, l) => l.findIndex((r2) => r2.key === r.key) === i,
    ),
  })),
  on(referenceActions.updateCitation, (state, { citation }) => ({
    ...state,
    citations: [citation, ...state.citations].filter(
      (c1, index, array) => index === array.findIndex((c2) => c1.citationID === c2.citationID),
    ),
  })),
  on(referenceActions.syncCitationsFromDocument, (state, action) => ({
    ...state,
    citations: mergeCitations(state.citations, action.citations),
  })),
  on(referenceActions.setActiveReferenceId, (state, { referenceId }) => ({
    ...state,
    activeReferenceId: referenceId,
  })),
  on(referenceActions.setReferenceIdToEdit, (state, { referenceId }) => ({
    ...state,
    referenceIdToEdit: referenceId,
  })),
  on(referenceActions.removeReference, (state, { referenceId }) => {
    const normalizedReferenceId = referenceId?.trim();
    if (!normalizedReferenceId) {
      return state;
    }

    const removedReferences = state.references.filter((reference) =>
      matchesReferenceId(reference, normalizedReferenceId),
    );
    if (removedReferences.length === 0) {
      return state;
    }

    const removedIds = new Set<string>(
      removedReferences.flatMap((reference) => getReferenceIds(reference)),
    );

    const references = state.references.filter(
      (reference) => !matchesReferenceId(reference, normalizedReferenceId),
    );
    const citations = (state.citations || [])
      .map((citation) => {
        const citationItems = (citation?.citationItems || []).filter((item) => {
          const itemId = typeof item?.id === 'string' ? item.id.trim() : '';
          const itemDataId = typeof item?.itemData?.id === 'string' ? item.itemData.id.trim() : '';
          return !removedIds.has(itemId) && !removedIds.has(itemDataId);
        });
        return { ...citation, citationItems };
      })
      .filter((citation) => (citation?.citationItems || []).length > 0);

    const activeReferenceId =
      state.activeReferenceId && removedIds.has(state.activeReferenceId)
        ? null
        : state.activeReferenceId;
    const referenceIdToEdit =
      state.referenceIdToEdit && removedIds.has(state.referenceIdToEdit)
        ? null
        : state.referenceIdToEdit;

    return {
      ...state,
      references,
      citations,
      activeReferenceId,
      referenceIdToEdit,
    };
  }),
  on(referenceActions.clearReferenceIdToEdit, (state) => ({
    ...state,
    referenceIdToEdit: null,
  })),
  on(referenceActions.updateReferenceRawString, (state, { key, rawCitationString }) => ({
    ...state,
    references: (state.references || []).map((reference) => {
      if (reference.key !== key) return reference;
      return {
        ...reference,
        ...(reference.csl
          ? {
              csl: {
                ...reference.csl,
                rawCitation: rawCitationString,
              },
            }
          : {}),
      };
    }),
  })),
  on(referenceActions.syncReferenceRawCitation, (state, { referenceId, rawCitation }) => ({
    ...state,
    references: (state.references || []).map((reference) =>
      matchesReferenceId(reference, referenceId) && reference.csl
        ? { ...reference, csl: { ...reference.csl, rawCitation } }
        : reference,
    ),
  })),
  on(referenceActions.setReferenceSortOrder, (state, { sortOrder }) => ({
    ...state,
    sortOrder,
  })),
);

export const selectReferenceManagementState = (state): ReferencesState =>
  state['reference-management'];

/**
 * Selects all the references uploaded to the project
 */
export const selectReferenceList = createSelector(
  selectReferenceManagementState,
  (state: ReferencesState) => state.references,
);

export const selectActiveReferenceId = createSelector(
  selectReferenceManagementState,
  (state: ReferencesState) => state.activeReferenceId,
);

export const selectReferenceIdToEdit = createSelector(
  selectReferenceManagementState,
  (state: ReferencesState) => state.referenceIdToEdit,
);

/**
 * Selects references/citations added in the document(editor) using referencePlugin
 */
export const selectCitationlist = createSelector(
  selectReferenceManagementState,
  (state: ReferencesState) => state.citations,
);

/** Selects a citation. */
export const selectCitation = (citationID: string) =>
  createSelector(selectReferenceManagementState, (state: ReferencesState) =>
    state.citations.find((citation) => citation.citationID === citationID),
  );

/**
 * Selects all the references used in the project
 */
export const selectUsedReferences = createSelector(
  selectReferenceManagementState,
  (state: ReferencesState) => {
    const referenceIds: string[] = state.citations
      .reduce(
        (list, { citationItems }) => [...list, ...citationItems.map((ci) => ci.id)],
        [] as string[],
      )
      .filter((id, index, array) => array.indexOf(id) === index);
    return referenceIds
      .map((id) => {
        const ref = state.references.find((ref) => ref.key === id);
        if (!ref) {
          console.warn('Reference ' + id + ' was cited but not found in reference list');
        }
        return ref;
      })
      .filter((ref) => ref !== undefined && ref.csl !== undefined);
  },
);

/**
 * Selects CSL references that have not been cited in the document.
 * Plain text references are excluded; they are derived from the document itself.
 */
export const selectUncitedReferences = createSelector(
  selectReferenceList,
  selectUsedReferences,
  (allRefs, usedRefs) => {
    const usedKeys = new Set(usedRefs.map((r) => r?.key));
    return allRefs.filter((r) => r.csl !== undefined && !usedKeys.has(r.key));
  },
);

/**
 * True when every CSL reference in the project has at least one in-text citation.
 * Returns false when no CSL references have been loaded yet.
 */
export const selectAreAllReferencesCited = createSelector(
  selectReferenceList,
  selectUncitedReferences,
  (allRefs, uncitedRefs) => {
    const cslRefs = allRefs.filter((r) => r.csl !== undefined);
    return cslRefs.length > 0 && uncitedRefs.length === 0;
  },
);

export const selectReferenceSortOrder = createSelector(
  selectReferenceManagementState,
  (state: ReferencesState) => state.sortOrder,
);

/**
 * Selects CSL references ordered by citation appearance followed by uncited references.
 */
export const selectOrderedBibliographyReferences = createSelector(
  selectReferenceList,
  selectCitationlist,
  (references: Reference[], citations: CitationWithId[]) => {
    const cslReferences = (references || []).filter((reference) => Boolean(reference?.csl));
    if (cslReferences.length === 0) {
      return [];
    }

    const referenceById = new Map<string, Reference>();
    for (const reference of cslReferences) {
      for (const id of getReferenceIds(reference)) {
        referenceById.set(id, reference);
      }
    }

    const ordered: Reference[] = [];
    const seenKeys = new Set<string>();
    for (const citation of citations || []) {
      for (const item of citation?.citationItems || []) {
        const citationId = typeof item?.id === 'string' ? item.id.trim() : '';
        const citationItemDataId =
          typeof item?.itemData?.id === 'string' ? item.itemData.id.trim() : '';
        const reference = referenceById.get(citationId) || referenceById.get(citationItemDataId);
        if (!reference?.key || seenKeys.has(reference.key)) {
          continue;
        }
        seenKeys.add(reference.key);
        ordered.push(reference);
      }
    }

    for (const reference of cslReferences) {
      if (!reference?.key || seenKeys.has(reference.key)) {
        continue;
      }
      seenKeys.add(reference.key);
      ordered.push(reference);
    }

    return ordered;
  },
);

export const referenceMangementReducer: ActionReducer<ReferencesState, any> = (
  state: ReferencesState | undefined,
  action: Action,
) => {
  return referenceReducer(state, action);
};
